import {
    Box, Button, FormControl,
    FormErrorMessage,
    FormLabel, HStack, Icon, Image, Input,
    Select, SimpleGrid, Text, useColorModeValue, VStack
} from '@chakra-ui/react'
import { Field, Form, Formik } from 'formik'
import { useContext, useState } from 'react'
import { MdOutlinePermMedia } from "react-icons/md"
import {
    PiChats,
    PiEnvelopeSimple, PiFoldersLight, PiHardDrives, PiImage, PiImages, PiMagnifyingGlass, PiMegaphoneSimple, PiNotepad, PiShareNetwork, PiSquaresFour, PiUpload, PiUsersThree, PiVideoLight, PiYoutubeLogo
} from "react-icons/pi"
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from "yup"
import RegionList from '../../../utils/regionList'
import useToast from '../../hooks/useToast'
import { UserContext } from '../../providers'

const SPACE_BETWEEN_FORM_ELEMENTS = 10;

const ucFirst = (name: string): string =>
    name.charAt(0).toUpperCase() + name.slice(1)

const FormWizard = () => {
    const [step, setStep] = useState(1);
    const { user } = useContext(UserContext)
    const toast = useToast();

    const handleNext = () => setStep(step + 1);
    const handleBack = () => setStep(step - 1);

    // Store all wizard answers in state
    const [imageKitID, setImageKitId] = useState(user?.imagekitId ?? "");
    const [region, setRegion] = useState("");
    const [role, setRole] = useState("");
    const [objective, setObjective] = useState([]);
    const [hearAboutUs, setHearAboutUs] = useState([]); // NEW

    const { request } = useFetch({
        cachePolicy: CachePolicies.NO_CACHE,
    })

    const saveData = async (values, submitForm = false) => {
        if (values.imageKitID) {
            setImageKitId(values.imageKitID);
        }
        if (values.region) {
            setRegion(values.region);
        }
        if (values.role) {
            setRole(values.role);
        }
        if (values.objective) {
            setObjective(values.objective);
        }
        if (values.hearAboutUs) {
            setHearAboutUs(values.hearAboutUs);
        }

        if (submitForm) {
            await handleSubmit(values);
        }
    };

    const handleSubmit = async (values) => {
        if (!user?.clientNumber) return;

        const clientNumber = user.clientNumber;

        try {
            const response = (await request.post(
                `/api/clients/${clientNumber}/complete-setup`,
                values,
            ))

            if (!request.error) {
                window.trackEvent({ label: 'DashboardOnboardingComplete' })
                window.location.href = '/dashboard'
            } else {
                const error = response;
                const fieldSpecificError = error?.errors?.[0]?.msg;
                const field = error?.errors?.[0]?.param;
                const genericError = error?.errorMsg;
                const displayError = fieldSpecificError || genericError;
                toast({
                    title: "Error",
                    position: "top-right",
                    description: displayError ?? "Something went wrong. Please try again. If the issue persists, please contact support.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
                // Decide which step to redirect back to if there's a field-specific error
                if (field === "imageKitID" || field === "region") {
                    setStep(2);
                } else if (field === "role" || field === "objective") {
                    setStep(1);
                } else if (field === "hearAboutUs") {
                    setStep(3);
                }
            }
        } catch (error) {
            toast({
                title: "Error",
                position: "top-right",
                description: "Something went wrong. Please try again. If the issue persists, please contact support.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    }

    return (
        <>
            {step === 1 && (
                <StepOne
                    onNext={handleNext}
                    saveDate={saveData}
                    role={role}
                    objective={objective}
                />
            )}
            {step === 2 && (
                <StepTwo
                    onBack={handleBack}
                    onNext={handleNext}
                    saveDate={saveData}
                    imageKitID={imageKitID}
                    region={region}
                    role={role}
                    objective={objective}
                />
            )}
            {step === 3 && (
                <StepThree
                    onBack={handleBack}
                    saveDate={saveData}
                    role={role}
                    objective={objective}
                    imageKitID={imageKitID}
                    region={region}
                    hearAboutUs={hearAboutUs}
                />
            )}
        </>
    );
};

/* --------------------
   STEP 1
-------------------- */
const StepOne = ({ onNext, role, objective, saveDate }) => {
    const { user } = useContext(UserContext)

    const roles = getRoles({ user });
    const objectives = getObjective({ user });

    // Validation schema using Yup
    const validationSchema = Yup.object().shape({
        role: Yup.string()
            .required("Role is required.")
            .oneOf(roles, "Invalid role."),
        objective: Yup.array()
            .min(1, "At least one objective is required.")
            .of(Yup.string().oneOf(objectives.map(obj => obj.title), "Invalid objective.")),
    });

    return (
        <VStack align={"left"} spacing={SPACE_BETWEEN_FORM_ELEMENTS}>
            <Text color={"appCoolGrey"}>1/3</Text>
            <Box>
                <Formik
                    initialValues={{ role, objective }}
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                        saveDate(values);
                        onNext();
                    }}
                >
                    {({ touched, values, errors, setFieldValue }) => (
                        <Form>
                            <FormControl
                                isInvalid={Boolean(errors.role && touched.role)}
                                mb={SPACE_BETWEEN_FORM_ELEMENTS}
                            >
                                <FormLabel>What’s your role at the company?</FormLabel>
                                <Text mb={4} color={"appSlate"}>
                                    Please select the option that best describes your role.
                                </Text>
                                <Field
                                    width={"50%"}
                                    as={Select}
                                    name="role"
                                    placeholder="Select role"
                                >
                                    {roles.map((roleName, index) => (
                                        <option key={index} value={roleName}>
                                            {roleName}
                                        </option>
                                    ))}
                                </Field>
                                <FormErrorMessage>{errors.role}</FormErrorMessage>
                            </FormControl>

                            <FormControl
                                isInvalid={Boolean(errors.objective && touched.objective)}
                                mb={SPACE_BETWEEN_FORM_ELEMENTS}
                            >
                                <FormLabel>What’s your main objective to use ImageKit?</FormLabel>
                                <Text mb={4} color={"appSlate"}>
                                    Select all that apply
                                </Text>
                                <SimpleGrid columns={3} spacing={6} className="objective-list">
                                    {objectives.map(({ title, icon }, index) => (
                                        <ObjectiveCard
                                            initialState={values.objective.includes(title)}
                                            title={title}
                                            icon={icon}
                                            key={index}
                                            onChange={(isSelected) => {
                                                const updatedObjectives = isSelected
                                                    ? [...values.objective, title]
                                                    : values.objective.filter((obj) => obj !== title);
                                                setFieldValue("objective", updatedObjectives);
                                            }}
                                        />
                                    ))}
                                </SimpleGrid>
                                <FormErrorMessage>{errors.objective}</FormErrorMessage>
                            </FormControl>

                            <HStack>
                                <Button type="submit">
                                    Next
                                </Button>
                            </HStack>
                        </Form>
                    )}
                </Formik>
            </Box>
        </VStack>
    );
}

/* --------------------
   STEP 2
-------------------- */
const StepTwo = ({ onBack, onNext, imageKitID, region, saveDate, role, objective }) => {
    // Validation schema using Yup
    const validationSchema = Yup.object().shape({
        imageKitID: Yup.string()
            .matches(/^[a-zA-Z0-9]+$/, "Enter a valid ID. No special characters allowed.")
            .required("ImageKit ID is required."),
        region: Yup.string().required("Please select a region."),
    });

    return (
        <VStack align={"left"} spacing={SPACE_BETWEEN_FORM_ELEMENTS}>
            <Text color={"appCoolGrey"}>2/3</Text>
            <Box>
                <Formik
                    initialValues={{ imageKitID, region, role, objective }}
                    validationSchema={validationSchema}
                    onSubmit={(values) => {
                        // Save data and go to Step 3 (do NOT submit the entire form here)
                        saveDate(values);
                        onNext();
                    }}
                >
                    {({ touched, values, errors, isSubmitting }) => (
                        <Form>
                            <FormControl
                                isInvalid={Boolean(errors.imageKitID && touched.imageKitID)}
                                mb={SPACE_BETWEEN_FORM_ELEMENTS}
                            >
                                <FormLabel>Set-up ImageKit ID</FormLabel>
                                <Text mb={4} color={"appSlate"}>
                                    ImageKit ID is an alphanumeric identifier that uniquely
                                    identifies your account and assets. No special characters allowed.
                                </Text>
                                <Field
                                    as={Input}
                                    name="imageKitID"
                                    placeholder="Enter your ImageKit ID"
                                    w={"50%"}
                                />
                                <FormErrorMessage>{errors.imageKitID}</FormErrorMessage>
                                <Box mt={4} color={"appSlate"}>
                                    <Text>
                                        The chosen ImageKit ID will be used in asset URLs:
                                    </Text>
                                    <Text>
                                        https://ik.imagekit.io/<Text as="span" color={"appBlue"}>{values.imageKitID}</Text>/path/to/myimage.jpg
                                    </Text>
                                </Box>
                            </FormControl>

                            <FormControl
                                isInvalid={Boolean(errors.region && touched.region)}
                                mb={SPACE_BETWEEN_FORM_ELEMENTS}
                            >
                                <FormLabel>Select your data processing and storage region</FormLabel>
                                <Text mb={4} color={"appSlate"}>
                                    The processing region is crucial as it determines where your media
                                    files are stored and processed. Choose your processing region
                                    based on integration strategy, compliance needs, and audience location.
                                </Text>
                                <Field as={Select} name="region" placeholder="Select region" w={"50%"}>
                                    {RegionList.map(x => (
                                        <option key={x.id} value={x.id}>
                                            {x.name}
                                        </option>
                                    ))}
                                </Field>
                                <FormErrorMessage>{errors.region}</FormErrorMessage>
                            </FormControl>

                            <HStack>
                                <Button
                                    onClick={() => {
                                        saveDate(values);
                                        onBack();
                                    }}
                                    variant="outline"
                                >
                                    Back
                                </Button>
                                <Button
                                    colorScheme="blue"
                                    type="submit"
                                    disabled={isSubmitting}
                                    isLoading={isSubmitting}
                                >
                                    Next
                                </Button>
                            </HStack>
                        </Form>
                    )}
                </Formik>
            </Box>
        </VStack>
    );
}

/* --------------------
   STEP 3
-------------------- */
const StepThree = ({
    onBack,
    saveDate,
    role,
    objective,
    imageKitID,
    region,
    hearAboutUs
}) => {
    // Options for "How did you hear about us?" 
    // Update these as needed
    const howHeardOptions = [
        { title: "Google / Bing", icon: PiMagnifyingGlass },
        { title: "Word of Mouth / Referral", icon: PiChats },
        { title: "Blog or Article", icon: PiNotepad },
        { title: "Social Media", icon: PiShareNetwork },
        { title: "YouTube", icon: PiYoutubeLogo },
        { title: "Online Advertisement", icon: PiMegaphoneSimple },
        { title: "Email / Newsletter", icon: PiEnvelopeSimple },
        { title: "Conference / Meetup", icon: PiUsersThree },
        { title: "Other", icon: PiSquaresFour },
    ];


    // Validation schema
    const validationSchema = Yup.object().shape({
        hearAboutUs: Yup.array()
            .min(1, "Please select at least one option.")
            .of(Yup.string().oneOf(howHeardOptions.map(obj => obj.title), "Invalid choice.")),
    });

    return (
        <VStack align={"left"} spacing={SPACE_BETWEEN_FORM_ELEMENTS}>
            <Text color={"appCoolGrey"}>3/3</Text>
            <Box>
                <Formik
                    initialValues={{
                        role,
                        objective,
                        imageKitID,
                        region,
                        hearAboutUs
                    }}
                    validationSchema={validationSchema}
                    onSubmit={async (values) => {
                        // Final submission
                        await saveDate(values, true);
                    }}
                >
                    {({ touched, values, errors, setFieldValue, isSubmitting }) => (
                        <Form>
                            <FormControl
                                isInvalid={Boolean(errors.hearAboutUs && touched.hearAboutUs)}
                                mb={SPACE_BETWEEN_FORM_ELEMENTS}
                            >
                                <FormLabel>How did you hear about us?</FormLabel>
                                <Text mb={4} color={"appSlate"}>
                                    Select all that apply
                                </Text>
                                <SimpleGrid columns={3} spacing={6} className="objective-list">
                                    {howHeardOptions.map(({ title, icon }, index) => (
                                        <ObjectiveCard
                                            initialState={values.hearAboutUs.includes(title)}
                                            title={title}
                                            icon={icon}
                                            key={index}
                                            onChange={(isSelected) => {
                                                const updatedHeard = isSelected
                                                    ? [...values.hearAboutUs, title]
                                                    : values.hearAboutUs.filter((x) => x !== title);
                                                setFieldValue("hearAboutUs", updatedHeard);
                                            }}
                                        />
                                    ))}
                                </SimpleGrid>
                                <FormErrorMessage>{errors.hearAboutUs}</FormErrorMessage>
                            </FormControl>

                            <HStack>
                                <Button
                                    onClick={() => {
                                        saveDate(values);
                                        onBack();
                                    }}
                                    variant="outline"
                                >
                                    Back
                                </Button>
                                <Button
                                    colorScheme="blue"
                                    type="submit"
                                    disabled={isSubmitting}
                                    isLoading={isSubmitting}
                                >
                                    Submit
                                </Button>
                            </HStack>
                        </Form>
                    )}
                </Formik>
            </Box>
        </VStack>
    );
};

const ObjectiveCard = ({ onChange, title, icon, initialState = false }) => {
    // State to track if the card is toggled
    const [isToggled, setIsToggled] = useState(initialState);

    // Handler for the toggle action
    const handleToggle = () => {
        const newState = !isToggled;
        setIsToggled(newState);
        if (onChange) onChange(newState);
    };

    // Use theme colors for selected/unselected states
    const selectedBg = "#e9f4ff";
    const selectedBorder = "#e5edfb";
    const selectedText = "appBlue";

    const bgColor = isToggled ? selectedBg : useColorModeValue("white", "white");
    const borderColor = isToggled ? selectedBorder : useColorModeValue("gray.200", "gray.200");
    const textColor = isToggled ? selectedText : useColorModeValue("#4a5568", "#4a5568");
    const hoverBgColor = isToggled ? selectedBg : useColorModeValue("gray.100", "gray.100");

    return (
        <HStack
            className="objective-item"
            bg={bgColor}
            onClick={handleToggle}
            p={4}
            borderRadius="8px"
            cursor="pointer"
            borderWidth="1px"
            borderColor={borderColor}
            transition="background-color 0.2s ease, border-color 0.2s ease"
            _hover={{
                bg: hoverBgColor,
            }}
        >
            <Icon as={icon} color={textColor} w={6} h={6} />
            <Text color={textColor}>{title}</Text>
        </HStack>
    );
};

export const IDRegion = (props: any) => {
    const { user } = useContext(UserContext)
    return (
        <VStack spacing={8} w={"1024px"} m={"50px auto"}>
            <Box>
                <Image
                    src="https://ik.imagekit.io/ikmedia/logo/light_T4buIzohVH.svg"
                    alt="ImageKit logo"
                    height="35px"
                />
            </Box>

            <HStack spacing={4} w={"full"}>
                <Box>
                    <Image
                        src="https://ik.imagekit.io/ikmedia/see-you-soon.svg"
                        alt="Get Started Welcome"
                    />
                </Box>
                <VStack spacing={"7px"} align={"left"}>
                    <Text fontWeight={"semibold"} as={"h2"} color={"#1a202c"} fontSize={"18px"}>
                        Welcome aboard, {ucFirst(String(user?.userName.split(' ')[0]))}
                    </Text>
                    <Text color={'appSlate'} fontSize={"14px"}>
                        Help us get you started with a few quick details.
                    </Text>
                </VStack>
            </HStack>

            <Box w={"full"} boxShadow={"0 0 64px 0 rgba(0, 81, 175, 0.1)"} padding={8} borderRadius={"24px"}>
                <FormWizard />
            </Box>
        </VStack>
    )
};

const getRoles = ({ user }) => {
    const isDAMONLYPlan = isOnDAMONLYPlan({ user });
    if (isDAMONLYPlan) {
        return [
            "Category Manager / Site Merchandiser",
            "CEO / Founder",
            "Content Marketer",
            "Creative Director / Head of Marketing / Head of e-Commerce / Head of Digital",
            "Developers / Engineering Managers",
            "Digital Marketer / Paid Marketer",
            "Digital Agency",
            "Finance",
            "Graphic Designer / Photographer",
            "Marketing Manager",
            "Product Managers"
        ];
    } else {
        return [
            "Category Manager / Site Merchandiser",
            "CEO / Founder",
            "Creative Director / Head of Marketing / Head of e-Commerce / Head of Digital",
            "Digital Agency",
            "Digital Marketer / Paid Marketer / Content Marketer",
            "Engineering Manager",
            "Finance",
            "Freelance Developer",
            "Graphic Designer / Photographer",
            "Marketing Manager",
            "Product Manager",
            "Software or App Developer",
            "V.P. Engineering / Dir. of Engineering / Engineering Manager / CTO"
        ];
    }
}

const getObjective = ({ user }) => {
    const isDAMONLYPlan = isOnDAMONLYPlan({ user });
    let result;
    if (isDAMONLYPlan) {
        result = [
            {
                title: "Upload & manage assets",
                icon: MdOutlinePermMedia,
            },
            {
                title: "Organize & search assets",
                icon: PiHardDrives,
            },
            {
                title: "Sharing & collaboration",
                icon: PiUsersThree,
            },
            {
                title: "Upload & manage assets via API",
                icon: PiUpload,
            }
        ]
    } else {
        result = [
            {
                title: "Real-time image optimization, transformations & delivery",
                icon: PiImage,
            },
            {
                title: "Real-time video optimization, transformations & streaming",
                icon: PiVideoLight,
            },
            {
                title: "Creating rich, personalized media assets with overlays",
                icon: PiImages,
            },
            {
                title: "Digital Asset Management",
                icon: PiFoldersLight,
            },
            {
                title: "APIs for media upload & management",
                icon: PiUpload,
            }
        ]
    }
    // Common "Others" option
    result.push({
        title: "Others",
        icon: PiSquaresFour,
    });
    return result;
}

const isOnDAMONLYPlan = ({ user }) => {
    if (user?.pricingPlan?.planType === "DAM_ONLY") return true;
    return false;
}
